import './scrn-slider.css';

/**
 * SCRN Slider Frontend Runtime Engine v2.1
 * Accessible, GPU-accelerated, timeline-stagger animation slider with proportional stage scaling.
 * WCAG 2.2.2 compliant: pause button, aria-live, prefers-reduced-motion.
 * Zero external dependencies.
 */

(function () {
  'use strict';

  class SCRNSlider {
    constructor(element) {
      this.wrapper = element;
      this.config = JSON.parse(element.getAttribute('data-slider-config') || '{}');
      this.settings = this.config.settings || {};

      this.stageW = parseInt(element.getAttribute('data-stage-width') || this.settings.width || '1200', 10) || 1200;
      this.stageH = parseInt(element.getAttribute('data-stage-height') || this.settings.height || '550', 10) || 550;

      this.viewport = this.wrapper.querySelector('.scrn-slider-viewport');
      this.slideItems = Array.from(this.wrapper.querySelectorAll('.scrn-slide-item'));
      this.arrowsPrev = this.wrapper.querySelector('.scrn-slider-arrow.scrn-prev');
      this.arrowsNext = this.wrapper.querySelector('.scrn-slider-arrow.scrn-next');
      this.dots = Array.from(this.wrapper.querySelectorAll('.scrn-slider-dot'));
      this.progressBar = this.wrapper.querySelector('.scrn-progress-fill');
      this.pauseBtn = this.wrapper.querySelector('.scrn-slider-pause-btn');
      this.liveRegion = this.wrapper.querySelector('.scrn-slider-live-region');

      this.currentIndex = 0;
      this.totalSlides = this.slideItems.length;
      this.autoplayTimer = null;
      this.isPaused = false;

      // Respect prefers-reduced-motion: disable autoplay by default
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.reducedMotion) {
        this.settings.autoplay = { ...this.settings.autoplay, enabled: false };
      }

      if (this.totalSlides === 0) return;
      this.init();
    }

    updateScale() {
      if (!this.wrapper) return;
      const currentW = this.wrapper.clientWidth;
      if (currentW > 0) {
        let activeStageW = this.stageW || 1200;
        if (currentW <= 480) {
          activeStageW = 375;
        } else if (currentW <= 768) {
          activeStageW = 768;
        } else if (currentW <= 1024) {
          activeStageW = 1024;
        }
        const scale = currentW / activeStageW;
        this.wrapper.style.setProperty('--scrn-slider-scale', scale.toFixed(5));
      }
    }

    init() {
      // Dynamic Proportional Stage Scale
      this.updateScale();
      window.addEventListener('resize', () => this.updateScale());
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => this.updateScale());
        ro.observe(this.wrapper);
      }

      // ARIA setup
      this.wrapper.setAttribute('tabindex', '0');
      if (!this.wrapper.getAttribute('aria-label')) {
        this.wrapper.setAttribute('aria-label', 'Image carousel');
      }
      this.slideItems.forEach((slide, i) => {
        slide.setAttribute('aria-label', `Slide ${i + 1} of ${this.totalSlides}`);
      });

      // Arrows
      if (this.arrowsPrev) {
        this.arrowsPrev.setAttribute('aria-label', 'Previous slide');
        this.arrowsPrev.addEventListener('click', () => this.prevSlide());
      }
      if (this.arrowsNext) {
        this.arrowsNext.setAttribute('aria-label', 'Next slide');
        this.arrowsNext.addEventListener('click', () => this.nextSlide());
      }

      // Dots
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.setAttribute('role', 'button');
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      // Pause button (WCAG 2.2.2)
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-label', 'Pause slideshow');
        this.pauseBtn.addEventListener('click', () => this.togglePause());
      }

      // Touch / Swipe
      if (this.settings.touch_swipe !== false) {
        this.initTouchEvents();
      }

      // Keyboard navigation
      this.wrapper.addEventListener('keydown', (e) => this.handleKeyboard(e));

      // Hover pause
      if (this.settings.autoplay?.pause_on_hover) {
        this.wrapper.addEventListener('mouseenter', () => {
          if (!this.isPaused) this.stopAutoplay();
        });
        this.wrapper.addEventListener('mouseleave', () => {
          if (!this.isPaused) this.startAutoplay();
        });
      }

      // Deep link
      if (this.settings.deep_linking) {
        this.checkDeepLink();
      }

      // Lazy-load: preload active + adjacent
      this.lazyLoadSlide(this.currentIndex);
      this.lazyLoadSlide(this.currentIndex + 1);

      // Initial slide
      this.goToSlide(this.currentIndex, true);
    }

    lazyLoadSlide(index) {
      if (index < 0 || index >= this.totalSlides) return;
      const slide = this.slideItems[index];
      if (!slide) return;
      // Background images stored as data-lazy-bg
      const lazyBg = slide.getAttribute('data-lazy-bg');
      if (lazyBg && !slide.getAttribute('data-lazy-loaded')) {
        slide.style.backgroundImage = `url("${lazyBg}")`;
        slide.setAttribute('data-lazy-loaded', '1');
      }
      // Lazy img tags
      slide.querySelectorAll('img[data-lazy-src]').forEach((img) => {
        if (!img.src || img.src === window.location.href) {
          img.src = img.getAttribute('data-lazy-src');
          img.removeAttribute('data-lazy-src');
        }
      });
    }

    goToSlide(index, isInitial = false) {
      if (index < 0) index = this.settings.loop !== false ? this.totalSlides - 1 : 0;
      if (index >= this.totalSlides) index = this.settings.loop !== false ? 0 : this.totalSlides - 1;

      const prevSlide = this.slideItems[this.currentIndex];
      const nextSlide = this.slideItems[index];

      if (!isInitial && prevSlide) {
        prevSlide.classList.remove('is-active');
        this.resetLayerAnimations(prevSlide);
      }

      this.currentIndex = index;
      if (nextSlide) {
        nextSlide.classList.add('is-active');
      }

      // Lazy-load this slide + next
      this.lazyLoadSlide(index);
      this.lazyLoadSlide(index + 1);
      this.lazyLoadSlide(index - 1);

      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      // Announce to screen readers
      if (this.liveRegion && !isInitial) {
        this.liveRegion.textContent = `Slide ${index + 1} of ${this.totalSlides}`;
      }

      // Animate layers
      if (nextSlide) {
        this.animateLayers(nextSlide);
      }

      // Autoplay
      if (this.settings.autoplay?.enabled && !this.isPaused) {
        this.startAutoplay();
      }

      // Deep link
      if (this.settings.deep_linking && !isInitial && nextSlide) {
        const slideId = nextSlide.getAttribute('data-slide-id');
        if (slideId) history.replaceState(null, '', `#${slideId}`);
      }
    }

    animateLayers(slide) {
      const layers = slide.querySelectorAll('.scrn-slide-layer');
      layers.forEach((layer) => {
        const animDur = parseInt(layer.getAttribute('data-duration-in') || '600', 10);
        const animDelay = parseInt(layer.getAttribute('data-delay-in') || '0', 10);
        const outDur = parseInt(layer.getAttribute('data-duration-out') || '300', 10);
        const outOffset = parseInt(layer.getAttribute('data-offset-out') || '0', 10);
        const slideDur = parseInt(slide.getAttribute('data-duration') || '5000', 10);

        if (this.reducedMotion) {
          layer.style.opacity = '1';
          layer.style.transform = 'none';
          return;
        }

        layer.style.animationDuration = `${animDur}ms`;
        layer.style.animationDelay = `${animDelay}ms`;
        layer.classList.remove('is-animating-out');
        layer.classList.add('is-animating-in');

        if (this.settings.autoplay?.enabled) {
          const outDelay = slideDur - outOffset - outDur;
          if (outDelay > 0) {
            if (layer._exitTimer) clearTimeout(layer._exitTimer);
            layer._exitTimer = setTimeout(() => {
              layer.style.animationDuration = `${outDur}ms`;
              layer.style.animationDelay = '0ms';
              layer.classList.remove('is-animating-in');
              layer.classList.add('is-animating-out');
            }, outDelay);
          }
        }
      });
    }

    resetLayerAnimations(slide) {
      slide.querySelectorAll('.scrn-slide-layer').forEach((layer) => {
        if (layer._exitTimer) clearTimeout(layer._exitTimer);
        layer.classList.remove('is-animating-in', 'is-animating-out');
      });
    }

    nextSlide() { this.goToSlide(this.currentIndex + 1); }
    prevSlide() { this.goToSlide(this.currentIndex - 1); }

    togglePause() {
      this.isPaused = !this.isPaused;
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-label', this.isPaused ? 'Play slideshow' : 'Pause slideshow');
        this.pauseBtn.setAttribute('aria-pressed', this.isPaused ? 'true' : 'false');
        this.pauseBtn.classList.toggle('is-paused', this.isPaused);
      }
      if (this.isPaused) {
        this.stopAutoplay();
      } else if (this.settings.autoplay?.enabled) {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      const currentSlide = this.slideItems[this.currentIndex];
      const slideDelay = parseInt(currentSlide?.getAttribute('data-duration') || '5000', 10);
      const globalDelay = this.settings.autoplay?.delay || 5000;
      const delay = Math.max(1000, slideDelay > 0 ? slideDelay : globalDelay);

      if (this.progressBar) {
        this.progressBar.style.transition = 'none';
        this.progressBar.style.width = '0%';
        setTimeout(() => {
          this.progressBar.style.transition = `width ${delay}ms linear`;
          this.progressBar.style.width = '100%';
        }, 20);
      }

      this.autoplayTimer = setTimeout(() => this.nextSlide(), delay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) clearTimeout(this.autoplayTimer);
      if (this.progressBar) {
        this.progressBar.style.transition = 'none';
        this.progressBar.style.width = '0%';
      }
    }

    initTouchEvents() {
      let startX = 0, startY = 0;
      this.wrapper.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      this.wrapper.addEventListener('touchend', (e) => {
        const distX = e.changedTouches[0].clientX - startX;
        const distY = e.changedTouches[0].clientY - startY;
        if (Math.abs(distX) > 40 && Math.abs(distX) > Math.abs(distY)) {
          if (distX < 0) this.nextSlide();
          else this.prevSlide();
        }
      }, { passive: true });
    }

    handleKeyboard(e) {
      if (e.key === 'ArrowLeft') this.prevSlide();
      else if (e.key === 'ArrowRight') this.nextSlide();
      else if (e.key === ' ' || e.key === 'p') this.togglePause();
    }

    checkDeepLink() {
      const hash = window.location.hash.replace('#', '');
      if (!hash) return;
      const targetIndex = this.slideItems.findIndex(
        (slide) => slide.getAttribute('data-slide-id') === hash
      );
      if (targetIndex !== -1) this.currentIndex = targetIndex;
    }
  }

  const initAllSliders = () => {
    document.querySelectorAll('.scrn-slider-wrapper').forEach((el) => {
      if (!el._scrnSliderInit) {
        el._scrnSliderInit = new SCRNSlider(el);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSliders);
  } else {
    initAllSliders();
  }

  window.SCRNSlider = SCRNSlider;
})();
